import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";
import { riderService } from "../../../../services/riderService";
import {
  CreateInHouseRepairPayload, CreateInHouseRepairResult, MaintenanceNotification, Pagination,
  PreventiveMaintenance, PreventiveMaintenanceAnalytics, PreventiveMaintenanceFilters, PreventiveScheduleApiRow,
} from "../types";

const BASE_URL = "/maintenance/preventive-schedules";
const riderIds = new Map<string, { value: string; expiresAt: number }>();
const riderLookups = new Map<string, Promise<string>>();
const publicRiderId = async (id?: string | null): Promise<string> => {
  if (!id) return "-";
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return id;
  const cached = riderIds.get(id);
  if (cached && cached.expiresAt > Date.now()) return cached.value;
  const pending = riderLookups.get(id);
  if (pending) return pending;
  const request = riderService.getRiderById(id).then((response) => {
    const value = response.data?.publicRiderId || "Unavailable";
    if (riderIds.size >= 500) riderIds.clear();
    riderIds.set(id, { value, expiresAt: Date.now() + 60_000 });
    return value;
  }).catch(() => "Unavailable").finally(() => riderLookups.delete(id));
  riderLookups.set(id, request);
  return request;
};
interface PreventiveSummary {
  analytics: PreventiveMaintenanceAnalytics;
  notifications: MaintenanceNotification[];
  sync?: { status: "current" | "delayed"; message?: string; lastSyncedAt: string | null };
}
const istDay = (date: number) => Math.floor((date + 330 * 60_000) / 86_400_000);
const daysUntil = (date: string) => istDay(new Date(date).getTime()) - istDay(Date.now());

const deriveStatus = (row: PreventiveScheduleApiRow): PreventiveMaintenance["status"] => {
  if (row.status === "INACTIVE") return "INACTIVE";
  const days = daysUntil(row.nextServiceDate);
  if (days < 0) return "OVERDUE";
  if (days === 0) return "DUE_TODAY";
  return days <= 7 ? "DUE_SOON" : "ACTIVE";
};

const mapSchedule = (row: PreventiveScheduleApiRow): PreventiveMaintenance => {
  const currentCycle = row.cycles?.find((cycle) => cycle.status === "IN_HOUSE_CREATED") ||
    row.cycles?.find((cycle) => Math.floor(new Date(cycle.dueDate).getTime() / 86_400_000) === istDay(new Date(row.nextServiceDate).getTime())) || null;
  return {
    id: row.id, maintenanceNumber: row.code, vehicleId: row.vehicleId,
    vehicleNumber: row.vehicleNumber || row.vehicleId, currentRiderId: row.currentRiderId,
    warehouseId: row.warehouseId, deploymentDate: row.deploymentDate,
    lastServiceDate: row.lastServiceDate, nextServiceDate: row.nextServiceDate,
    intervalDays: row.serviceIntervalDays, lastTicketId: row.lastTicketId,
    lastTicketCode: row.cycles?.find((cycle) => cycle.generatedTicketId === row.lastTicketId)?.generatedTicket?.code,
    status: deriveStatus(row), recordStatus: row.status, remarks: row.remarks,
    createdById: row.createdById, updatedById: row.updatedById,
    createdAt: row.createdAt, updatedAt: row.updatedAt, currentCycle,
    generatedTicket: currentCycle?.generatedTicket || null,
  };
};

const mapScheduleWithRider = async (row: PreventiveScheduleApiRow): Promise<PreventiveMaintenance> => ({
  ...mapSchedule(row), riderDisplayId: await publicRiderId(row.currentRiderId),
});

const preventiveMaintenanceService = {
  async getSummary(force = false): Promise<PreventiveSummary> {
    const response = await sparePartsApi.get<ApiResponse<PreventiveSummary>>(`${BASE_URL}/summary`, { params: { refresh: force || undefined } });
    return response.data.data;
  },
  async getPreventiveMaintenances(filters: PreventiveMaintenanceFilters = {}, force = false): Promise<{ items: PreventiveMaintenance[]; pagination: Pagination }> {
    if (force) riderIds.clear();
    const response = await sparePartsApi.get<ApiResponse<PreventiveScheduleApiRow[]>>(BASE_URL, { params: { ...filters, refresh: force || undefined } });
    return { items: await Promise.all((response.data.data || []).map(mapScheduleWithRider)), pagination: response.data.meta as Pagination };
  },
  async getPreventiveMaintenanceById(id: string): Promise<PreventiveMaintenance> {
    const response = await sparePartsApi.get<ApiResponse<PreventiveScheduleApiRow>>(`${BASE_URL}/${id}`);
    return mapScheduleWithRider(response.data.data);
  },
  async createInHouseRepair(id: string, payload: CreateInHouseRepairPayload): Promise<CreateInHouseRepairResult> {
    const response = await sparePartsApi.post<ApiResponse<CreateInHouseRepairResult>>(`${BASE_URL}/${id}/create-in-house-ticket`, payload);
    return response.data.data;
  },
};

export default preventiveMaintenanceService;
